import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  Router,
  UrlTree
} from '@angular/router';
import { HotelService } from '../services/hotel.service';
import { UserService } from '../services/user.service';
import { ConfirmationService } from '../services/confirmation.service';
import { DialogService } from '../services/dialog.service';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class HotelSelectionUrlGuard implements CanActivate {
  constructor(
    private hotelService: HotelService,
    private userService: UserService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const hotelId = route.paramMap.get('hotelId');

    if (!hotelId || hotelId === 'null') {
      return this.translate.get('notifications.guards.invalid_hotel').pipe(
        switchMap((message: string) => {
          this.confirmationService.swalError(message);
          return of(this.router.parseUrl('/'));
        })
      );
    }

    // Already the active hotel — allow immediately.
    if (this.hotelService.hotelId === hotelId) {
      return of(true);
    }

    // Hotel was previously loaded and is cached — switch instantly, no API call.
    if (this.hotelService.isHotelCached(hotelId)) {
      return this.hotelService.initializeSelectedHotel(hotelId).pipe(
        map(() => true),
        catchError(() => of(this.router.parseUrl('/')))
      );
    }

    // Unknown hotel ID — try to load it via API first.
    // If the user has access, switch to that hotel and allow navigation.
    // If access is denied (403/404), show an error then open the hotel selection dialog.
    return this.hotelService.initializeSelectedHotel(hotelId).pipe(
      map(() => true),
      catchError(() => {
        this.confirmationService.swalError(this.translate.instant('notifications.guards.hotel_not_found'));
        return this.openHotelSelectionDialog();
      })
    );
  }

  private openHotelSelectionDialog(): Observable<boolean | UrlTree> {
    return this.dialogService.openHotelSelectionDialog({ disableClose: true }).afterClosed().pipe(
      switchMap((selectedHotelId: string | undefined) => {
        if (!selectedHotelId) {
          // User closed without selecting — go to current hotel or login.
          const currentHotelId = this.hotelService.hotelId;
          return of(currentHotelId
            ? this.router.createUrlTree(['/', currentHotelId, 'dashboard'])
            : this.router.createUrlTree(['/account/login']));
        }

        // Initialize the hotel before redirecting so the guard recognises it on
        // the next navigation and doesn't open the dialog a second time.
        return this.hotelService.initializeSelectedHotel(selectedHotelId).pipe(
          map(() => this.router.createUrlTree(['/', selectedHotelId])),
          catchError(() => {
            const currentHotelId = this.hotelService.hotelId;
            return of(currentHotelId
              ? this.router.createUrlTree(['/', currentHotelId, 'dashboard'])
              : this.router.createUrlTree(['/account/login']));
          })
        );
      })
    );
  }
}
